'use client';

/**
 * 한 글의 **누적 통계 추이** — 잣대 하나를 고르고 D+N 축으로 본다.
 *
 * 글을 여는 자리(상세 다이얼로그·소재 다이얼로그)가 함께 쓰는 하나다.
 *
 * ⚠️ `post.metrics` 가 날짜별 스냅샷 지도(`{YYYYMMDD: {...}}`)여야 그려진다 — 없으면 아무것도 안 그린다.
 * ⚠️ D+N 은 «경과 24시간»이 아니라 **UTC 캘린더 날짜 차이**다.
 */

import type { Theme, SxProps } from '@mui/material/styles';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { engagementRatePct } from 'src/utils/metrics/engagement-rate';
import { getSortAlignedMetricSnapshot } from 'src/utils/metrics/post-metric-snapshot';

import { RADIUS } from 'src/theme/ui-tokens';

import { ChartLine } from 'src/sections/examples/extra/chart-view/chart-line';


const METRIC_CHART_OPTIONS = [
  { value: 'totalReaction', label: '총반응' },
  { value: 'viewCount', label: '조회수' },
  { value: 'likeCount', label: '좋아요' },
  { value: 'commentCount', label: '댓글' },
  { value: 'savedCount', label: '저장' },
  { value: 'shareCount', label: '공유' },
  { value: 'downloadCount', label: '다운로드' },
  { value: 'engagementRate', label: 'E/R' },
];


type Props = {
  post: any;
  /** 커스텀 서치 등 `metricsSort` 기준으로 볼 때 — D+8 초과를 자른다 */
  useMetricDetailSort?: boolean;
  /** 제목 — 없으면 「누적 통계」 */
  title?: string;
  /** 바깥 여백은 부르는 쪽이 준다 — 블록은 제 바깥 여백을 갖지 않는다 */
  sx?: SxProps<Theme>;
};

export default function PostMetricTrend({ post, useMetricDetailSort = false, title = '누적 통계', sx }: Props) {
  const [metricKey, setMetricKey] = useState('totalReaction');

  const parseDateLikeToMs = (value: unknown): number | null => {
    if (value == null || value === '') return null;

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return null;
      return value < 1e12 ? value * 1000 : value;
    }

    const rawValue = String(value).trim();
    if (!rawValue) return null;

    if (/^\d{8}$/.test(rawValue)) {
      const year = Number(rawValue.slice(0, 4));
      const month = Number(rawValue.slice(4, 6)) - 1;
      const day = Number(rawValue.slice(6, 8));
      const parsedDate = new Date(year, month, day);
      if (!Number.isNaN(parsedDate.getTime())) return parsedDate.getTime();
    }

    const asNumber = Number(rawValue);
    if (Number.isFinite(asNumber) && rawValue.length >= 10) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }

    const normalizedValue = rawValue.replace(' ', 'T');
    const parsedDate = new Date(normalizedValue);
    if (!Number.isNaN(parsedDate.getTime())) return parsedDate.getTime();

    return null;
  };

  const metricChartData = (() => {
    if (!post?.metrics || Object.keys(post.metrics).length === 0) return null;

    const uploadDateMs = parseDateLikeToMs(post?.timestamp) ?? parseDateLikeToMs(post?.date) ?? parseDateLikeToMs(post?.indexedAt);
    const uploadYyyymmdd = uploadDateMs != null ? msToYyyymmddUtc(uploadDateMs) : null;
    const metricCutoffYyyymmdd = useMetricDetailSort
      ? resolveMetricSortCutoffYyyymmdd(post)
      : '';

    const metricEntries = Object.entries(post.metrics as Record<string, any>)
      .map(([dateKey, metric]) => {
        const metricDateMs = parseDateLikeToMs(metric?.requestDateTime) ?? parseDateLikeToMs(dateKey);
        return {
          dateKey,
          metric: (metric ?? {}) as any,
          metricDateMs,
        };
      })
      .filter(({ dateKey, metricDateMs }) => {
        const keyYyyymmdd = /^\d{8}$/.test(String(dateKey))
          ? String(dateKey)
          : metricDateMs != null
            ? msToYyyymmddUtc(metricDateMs)
            : String(dateKey ?? '').replace(/\D/g, '').slice(0, 8);

        if (metricCutoffYyyymmdd && keyYyyymmdd.length === 8 && keyYyyymmdd > metricCutoffYyyymmdd) {
          return false;
        }

        // metricScoreSort 표시: metricsSort 없어도 D+8 초과는 제외 (미리보기 등)
        if (useMetricDetailSort && uploadYyyymmdd && keyYyyymmdd.length === 8) {
          const calDiff = calendarDaysAfterYyyymmdd(uploadYyyymmdd, keyYyyymmdd);
          if (calDiff != null && calDiff > METRIC_SORT_CHART_MAX_DAY) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.metricDateMs != null && b.metricDateMs != null) return a.metricDateMs - b.metricDateMs;
        if (a.metricDateMs != null) return -1;
        if (b.metricDateMs != null) return 1;
        return a.dateKey.localeCompare(b.dateKey);
      });
    const selectedOption = METRIC_CHART_OPTIONS.find((option) => option.value === metricKey);
    const selectedLabel = selectedOption?.label ?? '총반응';

    const categories = metricEntries.map(({ dateKey, metricDateMs }) => {
      const metricYyyymmdd = /^\d{8}$/.test(String(dateKey))
        ? String(dateKey)
        : metricDateMs != null
          ? msToYyyymmddUtc(metricDateMs)
          : null;

      if (uploadYyyymmdd && metricYyyymmdd) {
        const calDiff = calendarDaysAfterYyyymmdd(uploadYyyymmdd, metricYyyymmdd);
        if (calDiff != null) {
          return `D+${Math.max(0, calDiff)}`;
        }
      }

      if (uploadDateMs == null || metricDateMs == null) return dateKey;
      const dayDiff = Math.floor((metricDateMs - uploadDateMs) / (24 * 60 * 60 * 1000));
      return `D+${Math.max(0, dayDiff)}`;
    });

    const seriesData = metricEntries.map(({ metric }) => {
      const like = metric.likeCount ?? 0;
      const comment = metric.commentCount ?? 0;
      const saved = metric.savedCount ?? 0;
      const share = metric.shareCount ?? 0;
      const download = metric.downloadCount ?? 0;
      const view = metric.viewCount ?? 0;

      if (metricKey === 'likeCount') return like;
      if (metricKey === 'commentCount') return comment;
      if (metricKey === 'viewCount') return view;
      if (metricKey === 'savedCount') return saved;
      if (metricKey === 'shareCount') return share;
      if (metricKey === 'downloadCount') return download;
      if (metricKey === 'engagementRate') {
        const rate = engagementRatePct(like, comment, view);
        return rate == null ? 0 : Number(rate.toFixed(2));
      }
      return like + comment + saved + share + download + view;
    });
    const seriesMaxValue = seriesData.length > 0 ? Math.max(...seriesData) : 0;
    const yAxisMax = getNiceAxisMax(seriesMaxValue);

    return {
      categories,
      yaxis: {
        min: 0,
        max: yAxisMax,
      },
      series: [
        {
          name: selectedLabel,
          data: seriesData,
        },
      ],
    };
  })();

  if (!metricChartData) return null;

  return (
    <Box sx={sx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Select
          size="small"
          value={metricKey}
          onChange={(event) => setMetricKey(event.target.value)}
          MenuProps={{
            disableScrollLock: true,
            keepMounted: true,
            sx: { zIndex: 12001 },
            PaperProps: { sx: { maxHeight: 320, zIndex: 12001, width: 100, minWidth: '100px !important' } },
          }}
          sx={{
            width: 100,
            minWidth: 100,
            borderRadius: RADIUS.md,
            bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 1, fontWeight: 600 },
          }}
        >
          {METRIC_CHART_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
      </Stack>
      <ChartLine chart={metricChartData} />
    </Box>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

function getNiceAxisMax(value: number) {
  if (!Number.isFinite(value) || value <= 0) return 1;

  const targetTicks = 6;
  const roughStep = value / targetTicks;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalizedStep = roughStep / magnitude;
  const stepCandidates = [1, 2, 2.5, 5, 10];
  const niceStep = stepCandidates.find((candidate) => normalizedStep <= candidate) ?? 10;
  const step = niceStep * magnitude;

  return Math.ceil(value / step) * step;
}

/** metricScoreSort 표시 시 누적 통계 최대 일차 (D+0 ~ D+8) */
const METRIC_SORT_CHART_MAX_DAY = 8;

/** UTC 기준 캘린더 일자 YYYYMMDD (차트 D+N — 경과 24시간이 아닌 날짜 차이) */
function msToYyyymmddUtc(ms: number) {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/** uploadYmd → metricYmd 사이의 UTC 캘린더 일 수 (같은 날 0, 다음 날 1, …) */
function calendarDaysAfterYyyymmdd(uploadYmd: unknown, metricYmd: unknown) {
  const u = String(uploadYmd);
  const m = String(metricYmd);
  if (!/^\d{8}$/.test(u) || !/^\d{8}$/.test(m)) return null;
  const t0 = Date.UTC(Number(u.slice(0, 4)), Number(u.slice(4, 6)) - 1, Number(u.slice(6, 8)));
  const t1 = Date.UTC(Number(m.slice(0, 4)), Number(m.slice(4, 6)) - 1, Number(m.slice(6, 8)));
  return Math.round((t1 - t0) / 86400000);
}

/** metricsSort(구 metricDetailSort) / metricScoreSort 기준 컷오프 YYYYMMDD */
function resolveMetricSortCutoffYyyymmdd(post: any) {
  const metricScoreSort = post?.metricScoreSort;
  const sortAligned = getSortAlignedMetricSnapshot(post);
  const candidates = [
    sortAligned?.metricDate,
    sortAligned?.requestDateTime,
    metricScoreSort && typeof metricScoreSort === 'object' ? metricScoreSort.metricDate : null,
    metricScoreSort && typeof metricScoreSort === 'object' ? metricScoreSort.requestDateTime : null,
  ];
  for (const raw of candidates) {
    const digits = String(raw ?? '').replace(/\D/g, '');
    if (digits.length >= 8) return digits.slice(0, 8);
  }
  return '';
}
